import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, ErrorMessage, Field } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Button, Row, Col, Label, FormGroup } from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../../../Common/CustomModal';
import logo from '../../../../assets/images/project-img/rites_logo.png';
import FormHeader from '../../../Common/FormHeader';
import EmptyCard from '../../../Common/EmptyCard';
import { fetchAllUnderFrameType } from '../../../../store/page/frametype/frametypeThunk';
import {
  fetchLuggageRackParams,
  storeLuggageRack,
  checkRecordExist,
  fetchShellNo,
} from '../../../../store/page/furnishing/furnishingThunk';
import { fetchShiftTypes } from '../../../../store/page/material/materialThunk';

const rectificationOptions = [
  { value: 'rectified', label: 'Rectified' },
  { value: 'not_rectified', label: 'Not Rectified' },
  { value: 'max_rectified', label: 'Rectification to Maximum Extent' },
  { value: 'not_rectifiable', label: 'Not Rectifiable Defect' },
];


const AddLuggageRack = ({ toggle, isOpen, editData, onSuccess }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedShellType, setSelectedShellType] = useState('');
  const [flowStatus, setFlowStatus] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [shellNoOptions, setShellNoOptions] = useState([]);
  const [shellNo, setShellNo] = useState('');
  //   const [shellTypeChanged, setShellTypeChanged] = useState(false);
  const [isShellNoLoading, setIsShellNoLoading] = useState(false);
  const fileRefs = useRef({});

  // States for record existence check
  const [recordExists, setRecordExists] = useState(false);
  const [isCheckingRecord, setIsCheckingRecord] = useState(false);
  const [recordCheckError, setRecordCheckError] = useState('');

  // Get data from Redux store
  const underframeData = useSelector(
    (state) => state?.frametypesReducer?.all_uf_types || state?.frametypesReducer?.uf_types || [],
  );
  // Group coach types by train type - only Vande Bharat
  const vandeBharatCoachTypes = useMemo(() => {
    if (!underframeData || !Array.isArray(underframeData) || underframeData.length === 0) {
      console.log('No underframe data available');
      return [];
    }

    // Filter only Vande Bharat (vende-bharat) coach types
    const filtered = underframeData.filter((coach) => {
      const trainType = coach.train_type || coach.type || '';
      return trainType === 'vende-bharat';
    });

    return filtered;
  }, [underframeData]);

  //   const shellNumbers = useSelector((state) => state?.finalInspectionReducer?.shellNumbers || []);
  const params = useSelector((state) => state?.finalInspectionReducer?.luggageRackData?.data || []);

  // Fetch shell types on component mount
  useEffect(() => {
    dispatch(fetchAllUnderFrameType());
  }, [dispatch]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRecordExists(false);
      setRecordCheckError('');
      setShellNo('');
      setShellNoOptions([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadEditShellNo = async () => {
      if (!isOpen || !editData?.shell_type) return;

      const shellTypeId =
        typeof editData.shell_type === 'object'
          ? editData.shell_type.id || editData.shell_type._id
          : editData.shell_type;

      if (!shellTypeId) return;

      setIsShellNoLoading(true);
      try {
        const shellNoResult = await dispatch(fetchShellNo({ coach_type: shellTypeId })).unwrap();
        const options = (shellNoResult?.data || []).map((shell) => ({
          label: shell.shell_no,
          value: shell.shell_no,
        }));

        if (editData?.shell_no && !options.some((o) => o.value === editData.shell_no)) {
          options.push({
            label: editData?.shell_dtls?.form_no || editData.shell_no,
            value: editData.shell_no,
          });
        }

        setShellNoOptions(options);
      } catch (error) {
        console.error('Error fetching shell numbers for edit:', error);
      } finally {
        setIsShellNoLoading(false);
      }
    };

    loadEditShellNo();
  }, [isOpen, editData, dispatch]);

  // Function to check if record exists
  const checkRecordExistence = async (shellType, shellNoSelected, currentId = null) => {
    // Don't check if either value is empty
    if (!shellType || !shellNoSelected) {
      setRecordExists(false);
      setRecordCheckError('');
      return;
    }

    setIsCheckingRecord(true);
    setRecordCheckError('');

    try {
      const response = await dispatch(
        checkRecordExist({
          shell_type: shellType,
          shell_no: shellNoSelected,
          stage: 'luggage_rack',
          exclude_id: currentId,
        }),
      ).unwrap();

      const exists = response?.data?.data?.exists || false;
      console.log('Record existence check response:', response);
      setRecordExists(exists);
      if (exists) {
        setRecordCheckError('Shell no. already exists for the selected shell type');
      } else {
        setRecordCheckError('');
      }
    } catch (error) {
      console.error('Error checking record existence:', error);
      setRecordExists(false);
      setRecordCheckError('');
    } finally {
      setIsCheckingRecord(false);
    }
  };

  // Fetch shift options
  useEffect(() => {
    const loadShifts = async () => {
      setShiftLoading(true);
      try {
        const response = await dispatch(fetchShiftTypes()).unwrap();
        const shiftData = response?.data || response || [];
        const formattedShifts = Array.isArray(shiftData)
          ? shiftData.map((shift) => ({
              value: shift.id || shift.value || shift,
              label: shift.name || shift.label || shift,
            }))
          : [];

        setShiftOptions(formattedShifts);
      } catch (error) {
        console.error('Error fetching shifts:', error);
        toast.error('Failed to load shift options');
        setShiftOptions([
          { value: 'A', label: 'A' },
          { value: 'B', label: 'B' },
          { value: 'C', label: 'C' },
        ]);
      } finally {
        setShiftLoading(false);
      }
    };

    loadShifts();
  }, [dispatch]);

  // Fetch LuggageRack Params when Shell Type changes
  const fetchLuggageRackParamsData = async (shellType) => {
    if (!shellType) {
      setFormData(null);
      return;
    }

    setModalLoading(true);
    try {
      console.log('🔄 Fetching luggage rack params for coach_type:', shellType);
      const result = await dispatch(fetchLuggageRackParams({ coach_type: shellType })).unwrap();
      const data = result?.data || [];
      setFormData(data);
      console.log('📊 Form Data:', data);
    } catch (error) {
      console.error('❌ Error fetching luggage rack params:', error);
      toast.error('Failed to fetch form parameters');
    } finally {
      setModalLoading(false);
    }
  };

  const handleShellTypeChange = async (e, setFieldValue) => {
    const { value } = e.target;
    setFieldValue('shell_type', value);
    setFieldValue('shell_no', '');
    setFieldValue('shell_no_label', '');
    setShellNoOptions([]);
    setSelectedShellType(value);
    setRecordExists(false);
    setRecordCheckError('');
    // setShellTypeChanged(true);
    if (value) {
      fetchLuggageRackParamsData(value);
    //   setIsShellNoLoading(true);
      const shellNoResult = await dispatch(fetchShellNo({ coach_type: value })).unwrap();
      const options = (shellNoResult?.data || []).map((shell) => ({
        label: shell.shell_no,
        value: shell.shell_no,
      }));
      if (editData?.shell_no && !options.some((o) => o.value === editData.shell_no)) {
        options.push({
          label: editData?.shell_dtls?.form_no || editData.shell_no,
          value: editData.shell_no,
        });
      }
      setShellNoOptions(options);
    } else {
      setFormData(null);
      setIsShellNoLoading(false);
      //   setShellTypeChanged(false);
    }
  };

  // Process data from API response - Updated for the new structure
  const processApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((item,idx) => ({
      // id: item.id || `item-${Date.now()}-${Math.random()}`,
        id: item.id || `item-edit-${idx}`,
      description: item.description || '',
      specified_val: item.specified_val || '',
      extra_point: item.extra_point || '',
      obs_val: item.obs_val || [
        { key: 'OK', val: '1' },
        { key: 'NOT OK', val: '2' },
      ],
      status: '',
      status_key: '',
      remarks: '',
      location: '',
      rectification: '',
      image: '',
      image_url: null,
      is_defect: false,
    }));
  };

  const generateInitialValues = () => {
    const processEditData = (editDataParam) => {
      if (!editDataParam || !Array.isArray(editDataParam)) return [];
      return editDataParam.map((item,idx) => ({
        // id: item.id || `item-${Date.now()}-${Math.random()}`,
         id: item.id || `item-api-${idx}`,
        description: item.description || '',
        specified_val: item.specified_val || '',
        extra_point: item.extra_point || '',
        obs_val: item.obs_val || [
          { key: 'OK', val: '1' },
          { key: 'NOT OK', val: '2' },
        ],
        status: item.status || '',
        status_key: item.status_key || '',
        remarks: item.remarks || '',
        location: item.location || '',
        rectification: item.rectification || '',
        image: item.image || '',
        image_url: item.image_url || null,
        is_defect: item.is_defect || false,
      }));
    };

    if (editData) {
       let shellTypeId = '';
    if (editData?.shell_type && typeof editData.shell_type === 'object') {
      shellTypeId = editData.shell_type.id || editData.shell_type._id || '';
    } else {
      shellTypeId = editData?.shell_type || '';
    }
      return {
       shell_type: shellTypeId, 
        shift: editData?.shift || '',
        shell_no: editData?.shell_no || '',
        shell_no_label: editData?.shell_dtls?.form_no || editData?.shell_no || '',
        coach_no: editData?.coach_no || '',
        furnishing_no: editData?.furnishing_no || '',
        date: editData?.date || '',
        // confirming: editData?.confirming || '',
        confirming: editData?.confirming !== undefined && editData?.confirming !== null 
  ? String(editData.confirming) 
  : '',
        other_obs: editData?.other_obs || '',
        inspection_items: processEditData(editData.inspection_items || editData.items || []),
      };
    }

    const dataToProcess = formData || params;
    const processedItems = dataToProcess ? processApiData(dataToProcess) : [];

    return {
      shell_type: selectedShellType || '',
      shift: '',
      shell_no: shellNo || '',
      shell_no_label: shellNo || '',
      coach_no: '',
      furnishing_no: '',
      date: '',
      confirming: '',
      other_obs: '',
      inspection_items: processedItems,
    };
  };

  // Validation schema - coach_no is optional
  const validationSchema = Yup.object({
    shell_type: Yup.string().required('Shell Type is required'),
    shift: Yup.string().required('Shift is required'),
    shell_no: Yup.string().required('Shell No is required'),
    coach_no: Yup.string(),
    date: Yup.string().required('Date is required'),
    confirming: Yup.string().required('Please select Conforming or Non-Conforming'),
    inspection_items: Yup.array().of(
      Yup.object({
        status: Yup.string().required('Please select OK or NOT OK'),
      }),
    ),
  });

   const initialValues = generateInitialValues();
//  const initialValues = useMemo(
//   () => generateInitialValues(),
//   [editData, formData, params],
// );

  const getInspectionSummary = (values) => {
    const items = values?.inspection_items || [];
    const summary = {
      ok: 0,
      notOk: 0,
      pending: 0,
      na: 0,
    };

    items.forEach((item) => {
      (item?.params || []).forEach((param) => {
        const status = String(param?.status || '').trim();

        if (status === '1') {
          summary.ok += 1;
        } else if (status === '2') {
          summary.notOk += 1;
        } else if (status === '3') {
          summary.pending += 1;
        } else if (status === '4') {
          summary.na += 1;
        } else {
          summary.pending += 1;
        }
      });
    });

    return summary;
  };

  const convertToFormData = (data) => {
    const payload = new FormData();
    const summary = getInspectionSummary(data);
    if (data.id) {
      payload.append('id', data.id);
    }

    payload.append('summary[ok]', summary.ok);
    payload.append('summary[notOk]', summary.notOk);
    payload.append('summary[pending]', summary.pending);
    payload.append('summary[na]', summary.na);
    payload.append('summary[total_defects_count]', summary.notOk);
    payload.append('summary[remaining_defects_count]', summary.notOk);
    const appendFormData = (value, key) => {
      if (value === null || value === undefined) {
        payload.append(key, '');
        return;
      }
      if (value instanceof File) {
        payload.append(key, value);
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          appendFormData(item, `${key}[${index}]`);
        });
        return;
      }
      if (typeof value === 'object' && !(value instanceof Date) && !(value instanceof Blob)) {
        Object.keys(value).forEach((objKey) => {
          appendFormData(value[objKey], `${key}[${objKey}]`);
        });
        return;
      }
      if (typeof value === 'boolean') {
        payload.append(key, value ? '1' : '0');
        return;
      }
      payload.append(key, value);
    };

    Object.keys(data).forEach((key) => {
      appendFormData(data[key], key);
    });

    return payload;
  };

  const handleSubmit = async (values, { resetForm }) => {
    // Check if record exists before submitting
    if (recordExists) {
      toast.error('This Shell No already exists for the selected Shell Type');
      return;
    }

    // Check if all parameters have status selected
    let hasError = false;
    const items = values?.inspection_items || [];
    items.forEach((item, index) => {
      if (!item.status || item.status === '') {
        hasError = true;
        const description = item.description || `Item ${index + 1}`;
        toast.error(`Please select OK or NOT OK for: ${description.substring(0, 50)}...`);
      }
    });

    if (hasError) {
      return;
    }
    const data = {
      ...values,
      flow_status: flowStatus,
      id: editData?.id || editData?._id || '', 
    };

    console.log('📤 Submitting:', data);

    const payload = convertToFormData(data);
    try {
      const response = await dispatch(storeLuggageRack(payload)).unwrap();
      console.log('submit api response:', response);
      if (response?.status === true) {
        toast.success(response?.message || 'Inspection Report Saved Successfully!');
        if (onSuccess) onSuccess(payload);
        toggle();
        resetForm();
      } else {
        toast.error(response?.message || 'Failed to save inspection report');
      }
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error('Something went wrong');
    }
  };

  // const renderInspectionItems = (items, values, setFieldValue) => {
  //   if (!items || !Array.isArray(items) || items.length === 0) {
  //     return (
  //       <div className="text-center py-4">
  //         <p className="text-muted">No inspection items available for this shell type.</p>
  //       </div>
  //     );
  //   }

  //   return items.map((item) => {
  //     // Find the index of this item in the array
  //     const itemIndex = items.indexOf(item);
  //     const fieldPath = `inspection_items.${itemIndex}`;
  //     // const itemStatus = values?.inspection_items?.[itemIndex]?.status || '';
  //     // const isDefect = Number(itemStatus) === 2;
  //     const currentItem = values?.inspection_items?.[itemIndex];
  //     const itemStatus = currentItem?.status || '';
  //     // const isDefect = Number(itemStatus) === 2;
  //     const wasPreviouslyDefect =
  //       editData &&
  //       (currentItem?.rectification === 'rectification_required' ||
  //         currentItem?.rectification === 'not_rectified' ||
  //         currentItem?.rectification === 'max_rectified' ||
  //         currentItem?.rectification === 'not_rectifiable' ||
  //         currentItem?.rectification === 'rectified');
  //     const isDefectStatus = Number(itemStatus) === 2;
  //     const defectPoint = isDefectStatus || wasPreviouslyDefect;
  //     const fileKey = `item-${item.id || itemIndex}`;

  //     return (
  //       <div
  //         key={item.id || `item-${itemIndex}`}
  //         className="inspection-row mb-4 p-3 border rounded"
  //         style={{ backgroundColor: '#fff' }}
  //       >
  //         <Row>
  //           <Col lg="12">
  //             <div
  //               style={{ fontSize: '15px', fontWeight: '600', color: '#000', marginBottom: '10px' }}
  //             >
  //               {itemIndex + 1}. {item.description || 'No description provided'}
  //             </div>

  //             {/* Specified Value - if exists */}
  //             {item.specified_val && (
  //               <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
  //                 <strong>Specified Value:</strong>
  //                 <div
  //                   style={{
  //                     whiteSpace: 'pre-wrap',
  //                     marginTop: '4px',
  //                     padding: '8px',
  //                     background: '#f8f9fa',
  //                     borderRadius: '4px',
  //                   }}
  //                 >
  //                   {item.specified_val}
  //                 </div>
  //               </div>
  //             )}

  //             {/* Extra Point - if exists */}
  //             {item.extra_point && (
  //               <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
  //                 <strong>Extra Point:</strong> {item.extra_point}
  //               </div>
  //             )}
  //           </Col>
  //         </Row>

  //         <Row className="mt-2">
  //           <Col lg="12">
  //             {/* OK/NOT OK buttons */}
  //             <div className="d-flex gap-2 flex-wrap mb-2">
  //               <button
  //                 type="button"
  //                 className={`status-btn ${itemStatus === '1' ? 'active' : ''} ok-btn`}
  //                 // onClick={() => {
  //                 //     setFieldValue(`${fieldPath}.status`, '1');
  //                 //     setFieldValue(`${fieldPath}.status_key`, 'OK');
  //                 //     setFieldValue(`${fieldPath}.is_defect`, false);
  //                 //     setFieldValue(`${fieldPath}.rectification`, '');
  //                 // }}
  //                 onClick={() => {
  //                   setFieldValue(`${fieldPath}.status`, '1');
  //                   setFieldValue(`${fieldPath}.status_key`, 'OK');

  //                   setFieldValue(`${fieldPath}.is_defect`, false);
  //                   if (editData) {
  //                     setFieldValue(`${fieldPath}.rectification`, 'rectified');
  //                   } else {
  //                     setFieldValue(`${fieldPath}.rectification`, '');
  //                   }
  //                 }}
  //                 style={{
  //                   padding: '6px 20px',
  //                   border: '2px solid #cccccc',
  //                   borderRadius: '4px',
  //                   background: itemStatus === '1' ? '#f0f0f0' : 'transparent',
  //                   color: '#333333',
  //                   fontWeight: '600',
  //                   cursor: 'pointer',
  //                   transition: 'all 0.2s ease',
  //                 }}
  //               >
  //                 OK
  //               </button>
  //               <button
  //                 type="button"
  //                 className={`status-btn ${itemStatus === '2' ? 'active' : ''} not_ok`}
  //                 // onClick={() => {
  //                 //     setFieldValue(`${fieldPath}.status`, '2');
  //                 //     setFieldValue(`${fieldPath}.status_key`, 'NOT OK');
  //                 //     setFieldValue(`${fieldPath}.is_defect`, true);
  //                 //     setFieldValue(`${fieldPath}.rectification`, 'rectification_required');
  //                 // }}
  //                 onClick={() => {
  //                   setFieldValue(`${fieldPath}.status`, '2');
  //                   setFieldValue(`${fieldPath}.status_key`, 'NOT OK');
  //                   setFieldValue(`${fieldPath}.is_defect`, true);
  //                   setFieldValue(`${fieldPath}.rectification`, 'rectification_required');
  //                 }}
  //                 style={{
  //                   padding: '6px 20px',
  //                   border: '2px solid #cccccc',
  //                   borderRadius: '4px',
  //                   background: itemStatus === '2' ? '#f0f0f0' : 'transparent',
  //                   color: '#333333',
  //                   fontWeight: '600',
  //                   cursor: 'pointer',
  //                   transition: 'all 0.2s ease',
  //                 }}
  //               >
  //                 NOT OK
  //               </button>
  //             </div>
  //             <ErrorMessage
  //               name={`${fieldPath}.status`}
  //               component="div"
  //               className="text-danger small"
  //             />

  //             {/* Remark Field */}
  //             <div
  //               style={{ fontSize: '13px', color: '#333', marginBottom: '5px', marginTop: '10px' }}
  //             >
  //               <strong>Remark</strong>
  //             </div>
  //             <Field
  //               name={`${fieldPath}.remarks`}
  //               className="form-control"
  //               placeholder="Enter Remark"
  //             />

  //             {/* Location, Rectification Status, Upload File - Only when defect exists */}
  //             {defectPoint && (
  //               <>
  //                 <div className="mt-2">
  //                   <div style={{ fontSize: '13px', color: '#333', marginBottom: '5px' }}>
  //                     <strong>Location</strong>
  //                   </div>
  //                   <Field
  //                     name={`${fieldPath}.location`}
  //                     className="form-control"
  //                     placeholder="Enter Location"
  //                   />
  //                 </div>

  //                 <div className="mt-2">
  //                   <Label className="fw-bold text-danger">Rectification Status</Label>
  //                   <Field as="select" name={`${fieldPath}.rectification`} className="form-select">
  //                     <option value="rectification_required">Rectification Required</option>
  //                     {rectificationOptions.map((opt) => (
  //                       <option key={opt.value} value={opt.value}>
  //                         {opt.label}
  //                       </option>
  //                     ))}
  //                   </Field>
  //                 </div>

  //                 <div className="mt-2">
  //                   <Label className="fw-bold">Upload File</Label>
  //                   <div style={{ position: 'relative', width: '100%' }}>
  //                     <input
  //                       type="file"
  //                       className="form-control pe-5"
  //                       accept="image/*,.pdf"
  //                       ref={(el) => {
  //                         fileRefs.current[fileKey] = el;
  //                       }}
  //                       onChange={(event) => {
  //                         const file = event.currentTarget.files[0];
  //                         if (file) {
  //                           const maxSize = 2 * 1024 * 1024;
  //                           if (file.size > maxSize) {
  //                             toast.error('You can upload max 2 MB file only');
  //                             if (fileRefs.current[fileKey]) {
  //                               fileRefs.current[fileKey].value = null;
  //                             }
  //                             setFieldValue(`${fieldPath}.image`, '');
  //                             setFieldValue(`${fieldPath}.image_url`, null);
  //                             return;
  //                           }
  //                           const previewUrl = URL.createObjectURL(file);
  //                           setFieldValue(`${fieldPath}.image`, file);
  //                           setFieldValue(`${fieldPath}.image_url`, previewUrl);
  //                         }
  //                       }}
  //                     />
  //                   </div>
  //                   <div className="text-danger small">Note: Please upload files less than 2mb</div>
  //                   <ErrorMessage
  //                     name={`${fieldPath}.image`}
  //                     component="div"
  //                     className="text-danger mt-1"
  //                   />
  //                 </div>
  //               </>
  //             )}
  //           </Col>
  //         </Row>
  //       </div>
  //     );
  //   });
  // };

  // Check if save should be disabled
  
  const renderInspectionItems = (items, values, setFieldValue) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No inspection items available for this shell type.</p>
      </div>
    );
  }

  return items.map((item) => {
    const itemIndex = items.indexOf(item);
    const fieldPath = `inspection_items.${itemIndex}`;
    const currentItem = values?.inspection_items?.[itemIndex];
    const itemStatus = currentItem?.status || '';
    
    const wasPreviouslyDefect =
      editData &&
      (currentItem?.rectification === 'rectification_required' ||
        currentItem?.rectification === 'not_rectified' ||
        currentItem?.rectification === 'max_rectified' ||
        currentItem?.rectification === 'not_rectifiable' ||
        currentItem?.rectification === 'rectified');
    const isDefectStatus = Number(itemStatus) === 2;
    const defectPoint = isDefectStatus || wasPreviouslyDefect;
    const fileKey = `item-${item.id || itemIndex}`;

    return (
      <div
        key={item.id || `item-${itemIndex}`}
        className="inspection-row mb-4 p-3 border rounded"
        style={{ backgroundColor: '#fff' }}
      >
        <Row>
          <Col lg="12">
            <div
              style={{ fontSize: '15px', fontWeight: '600', color: '#000', marginBottom: '10px' }}
            >
              {itemIndex + 1}. {item.description || 'No description provided'}
            </div>

            {/* Specified Value - if exists */}
            {item.specified_val && (
              <div style={{ 
                fontSize: '13px', 
                color: '#555', 
                marginBottom: '10px',
                padding: '4px 8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                borderLeft: '3px solid #0d6efd'
              }}>
                <strong>DRG No.:</strong> {item.specified_val}
              </div>
            )}

            {/* Extra Point - if exists */}
            {item.extra_point && (
              <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
                <strong>Extra Point:</strong> {item.extra_point}
              </div>
            )}
          </Col>
        </Row>

        <Row className="mt-2">
          <Col lg="12">
            <div className="d-flex gap-2 flex-wrap mb-2">
              {item.obs_val?.map((option) => {
                let btnClass = '';
                if (option?.val === '1') btnClass = 'ok-btn';
                else if (option?.val === '2') btnClass = 'not_ok';
                else btnClass = 'na';
                
                return (
                  <button
                    type="button"
                    key={option.val}
                    className={`status-btn ${String(itemStatus) === String(option?.val) ? 'active' : ''} ${btnClass}`}
                    onClick={() => {
                      setFieldValue(`${fieldPath}.status`, String(option?.val));
                      setFieldValue(`${fieldPath}.status_key`, option?.key);
                      setFieldValue(`${fieldPath}.is_defect`, option?.val === '2' || option?.val === 2);

                      if (option?.val === '2' || option?.val === 2) {
                        setFieldValue(`${fieldPath}.rectification`, 'rectification_required');
                      } else {
                        setFieldValue(`${fieldPath}.rectification`, '');
                        setFieldValue(`${fieldPath}.location`, '');
                        setFieldValue(`${fieldPath}.image`, '');
                        setFieldValue(`${fieldPath}.image_url`, null);
                      }
                    }}
                  >
                    {option?.key}
                  </button>
                );
              })}
            </div>
            <ErrorMessage
              name={`${fieldPath}.status`}
              component="div"
              className="text-danger small"
            />
            <div
              style={{ fontSize: '13px', color: '#333', marginBottom: '5px', marginTop: '10px' }}
            >
              <strong>Remark</strong>
            </div>
            <Field
              name={`${fieldPath}.remarks`}
              className="form-control"
              placeholder="Enter Remark"
            />

            {defectPoint && (
              <>
                <div className="mt-2">
                  <div style={{ fontSize: '13px', color: '#333', marginBottom: '5px' }}>
                    <strong>Location</strong>
                  </div>
                  <Field
                    name={`${fieldPath}.location`}
                    className="form-control"
                    placeholder="Enter Location"
                  />
                </div>

                <div className="mt-2">
                  <Label className="fw-bold text-danger">Rectification Status</Label>
                  <Field as="select" name={`${fieldPath}.rectification`} className="form-select">
                    <option value="rectification_required">Rectification Required</option>
                    {rectificationOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Field>
                </div>

                <div className="mt-2">
                  <Label className="fw-bold">Upload File</Label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type="file"
                      className="form-control pe-5"
                      accept="image/*,.pdf"
                      ref={(el) => {
                        fileRefs.current[fileKey] = el;
                      }}
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        if (file) {
                          const maxSize = 2 * 1024 * 1024;
                          if (file.size > maxSize) {
                            toast.error('You can upload max 2 MB file only');
                            if (fileRefs.current[fileKey]) {
                              fileRefs.current[fileKey].value = null;
                            }
                            setFieldValue(`${fieldPath}.image`, '');
                            setFieldValue(`${fieldPath}.image_url`, null);
                            return;
                          }
                          const previewUrl = URL.createObjectURL(file);
                          setFieldValue(`${fieldPath}.image`, file);
                          setFieldValue(`${fieldPath}.image_url`, previewUrl);
                        }
                      }}
                    />
                    {currentItem?.image && (
                      <button
                        type="button"
                        onClick={() => {
                          setFieldValue(`${fieldPath}.image`, '');
                          setFieldValue(`${fieldPath}.image_url`, null);
                          if (fileRefs.current[fileKey]) {
                            fileRefs.current[fileKey].value = null;
                          }
                        }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          color: '#050b12',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          background: 'transparent',
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div className="text-danger small">Note: Please upload files less than 2mb</div>
                  <ErrorMessage
                    name={`${fieldPath}.image`}
                    component="div"
                    className="text-danger mt-1"
                  />
                  {/* ✅ Image Preview */}
                 {currentItem?.image_url && (
  <div className="mt-2">
    {/* ✅ New file uploaded (File object) */}
    {currentItem.image instanceof File && currentItem.image.type.startsWith('image') && (
      <img
        src={currentItem.image_url}
        alt="preview"
        style={{
          width: '120px',
          height: '120px',
          objectFit: 'cover',
          border: '1px solid #ccc',
          borderRadius: '6px',
        }}
      />
    )}
    {currentItem.image instanceof File && currentItem.image.type === 'application/pdf' && (
      <a
        href={currentItem.image_url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm btn-info"
      >
        View PDF
      </a>
    )}
    
    {/* ✅ Existing image from API (string URL) */}
    {!currentItem.image || typeof currentItem.image === 'string' && (
      <a
        href={currentItem.image_url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm btn-primary"
      >
        <i className="bi bi-eye me-1"></i> View Image
      </a>
    )}
  </div>
)}
                </div>
              </>
            )}
          </Col>
        </Row>
      </div>
    );
  });
};

  const isSaveDisabled = (isSubmitting) => {
    return isSubmitting || isCheckingRecord || recordExists;
  };

  return (
    <CustomModal
      toggle={toggle}
      isOpen={isOpen}
      size="xl"
      title={`${editData ? 'Edit' : 'Add'} Luggage Rack Inspection`}
      backdrop="static"
    >
      <FormHeader
        logo={logo}
        companyName="MODERN COACH FACTORY, RAEBARELI"
        title1="Quality Control Clearance Memo"
        title2="(Stage Audit inspection – Luggage rack VB)"
        docNo=" MCF/RBL/QMF/VB 7000D"
        revNo="00"
        date="21.07.2025"
        version="12/24"
      />
      <Formik
       key={editData?.id || 'new'}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <Row className="m-1 mt-3">
              <Col lg="3">
                <FormGroup>
                  <Label>
                    Shift <span className="text-danger">*</span>
                  </Label>
                  <Field as="select" name="shift" className="form-select" disabled={shiftLoading}>
                    <option value="">{shiftLoading ? 'Loading shifts...' : 'Select Shift'}</option>
                    {shiftOptions.map((shift) => (
                      <option key={shift.value} value={shift.value}>
                        {shift.label}
                      </option>
                    ))}
                  </Field>
                  {shiftLoading && (
                    <div className="text-muted small mt-1">
                      <i className="bi bi-spinner bi-spin me-1"></i>
                      Loading shift options...
                    </div>
                  )}
                  <ErrorMessage name="shift" component="div" className="text-danger" />
                </FormGroup>
              </Col>
              <Col lg="3">
                <FormGroup>
                  <Label>
                    Shell Type <span className="text-danger">*</span>
                  </Label>
                  <Field
                    as="select"
                    name="shell_type"
                    className="form-select"
                    disabled={!!editData}
                    onChange={(e) => handleShellTypeChange(e, setFieldValue)}
                  >
                    <option value="">Select Shell Type</option>
                    {underframeData &&
                    Array.isArray(underframeData) &&
                    underframeData.length > 0 ? (
                      vandeBharatCoachTypes.length > 0 ? (
                        vandeBharatCoachTypes.map((coach) => (
                          <option key={coach.id} value={coach.id}>
                            {coach.title || coach.name || 'Unnamed Coach'}
                          </option>
                        ))
                      ) : (
                        underframeData.map((coach) => (
                          <option key={coach.id} value={coach.id}>
                            {coach.title || coach.name || 'Unnamed Coach'}
                          </option>
                        ))
                      )
                    ) : (
                      <option value="">No coach types available</option>
                    )}
                  </Field>
                  <ErrorMessage name="shell_type" component="div" className="text-danger" />
                </FormGroup>
              </Col>
              <Col lg="3">
                <FormGroup>
                  <Label>
                    Shell No <span className="text-danger">*</span>
                  </Label>
                  <CreatableSelect
                    isClearable
                    options={shellNoOptions}
                    // options={shellNumbers.map((item) => ({
                    //   label: item,
                    //   value: item,
                    // }))}
                    // value={
                    //   values.shell_no ? { label: values.shell_no, value: values.shell_no } : null
                    // }
                    // value={
                    //     values.shell_no ? { label: values.shell_no, value: values.shell_no } : null
                    //   }
                    value={
                        values.shell_no
                          ? { label: values.shell_no_label || values.shell_no, value: values.shell_no }
                          : null
                      }
                    onChange={(selected) => {
                      const selectedValue = selected ? selected.value : '';
                      const selectedLabel = selected ? selected.label : '';
                      setFieldValue('shell_no', selectedValue);
                      setFieldValue('shell_no_label', selectedLabel);
                      setShellNo(selected ? selected?.value : '');
                      if (values.shell_type && selectedValue) {
                        checkRecordExistence(values.shell_type, selectedValue, editData?.id);
                      } else {
                        setRecordExists(false);
                        setRecordCheckError('');
                      }
                    }}
                    onCreateOption={(inputValue) => {
                      setFieldValue('shell_no', inputValue);
                       setFieldValue('shell_no_label', inputValue);
                      setShellNo(inputValue);
                      if (values.shell_type && inputValue) {
                        checkRecordExistence(values.shell_type, inputValue, editData?.id);
                      }
                    }}
                    isLoading={isShellNoLoading}
                  />
                  <ErrorMessage name="shell_no" component="div" className="text-danger" />
                  {/* Display record exists validation message */}
                  {recordCheckError && (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                      {recordCheckError}
                    </div>
                  )}
                  {isCheckingRecord && (
                    <div className="text-info mt-1" style={{ fontSize: '0.875rem' }}>
                      Checking if record exists...
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col lg="3">
                <FormGroup>
                  <Label>Coach No</Label>
                  <Field
                    type="text"
                    name="coach_no"
                    className="form-control"
                    placeholder="Enter Coach No"
                  />
                  <ErrorMessage name="coach_no" component="div" className="text-danger" />
                </FormGroup>
              </Col>
            </Row>

            <Row className="m-1 mt-2">
              <Col lg="12">
                <span style={{ fontSize: '13px', color: '#333' }}>
                  <strong>Drg No :</strong> 889-6-2-001 Alt-Nil
                </span>
              </Col>
            </Row>

            {!values?.shell_type ? (
              <EmptyCard selectField="Shell Type" />
            ) : modalLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 text-muted">Loading inspection parameters...</div>
              </div>
            ) : (
              <Row className="trace-card m-1">
                <div className="d-flex justify-content-between align-items-center trace-card-header mb-3 col-md-12">
                  <h5 className="mb-0">Luggage Rack Inspection Items</h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Date field in header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Label className="mb-0" style={{ fontSize: '14px', fontWeight: '500',color: '#fff' }}>
                        Date:
                      </Label>
                      <Field name="date">
                        {({ field }) => (
                          <input
                            type="date"
                            {...field}
                            className="form-control"
                            style={{ padding: '4px 8px', fontSize: '14px', width: 'auto' }}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="date"
                        component="div"
                        className="text-danger"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="inspection-actions d-block">
                  {renderInspectionItems(values.inspection_items, values, setFieldValue)}
                </div>
                <div>
                  <Row className="trace-card m-1 mt-3">
                    <Col
                      md="12"
                      className="d-flex justify-content-between align-items-center"
                      style={{
                        background: '#E65100',
                        borderRadius: '13px 13px 0 0',
                        padding: '10px 15px',
                      }}
                    >
                      <h5 className="mb-0" style={{ fontWeight: 700, color: '#fff' }}>
                        Other observations
                      </h5>
                    </Col>
                    <Field
                      as="textarea"
                      name="other_obs"
                      placeholder="Enter additional observations, deviations, or notes for this inspection..."
                      className="form-control"
                      style={{ minHeight: '200px', boxShadow: 'none', border: '0px' }}
                    />
                    <ErrorMessage name="other_obs" component="div" className="text-danger" />
                  </Row>
                </div>
                <div className="mt-4 border-top pt-3">
                  <Row className="mt-3">
                    <Col lg="12" className="d-flex flex-column align-items-end">
                      <div className="d-flex gap-2">
                        <div className="form-check m-1">
                          <Field
                            type="radio"
                            name="confirming"
                            value="1"
                            className="form-check-input"
                            id="confirming"
                            onClick={(e) => setFlowStatus(Number(e.target.value))}
                          />
                          <Label className="form-check-label" htmlFor="confirming">
                            Conforming
                          </Label>
                        </div>
                        <div className="form-check">
                          <Field
                            type="radio"
                            name="confirming"
                            value="0"
                            className="form-check-input"
                            id="nonConfirming"
                            onClick={(e) => setFlowStatus(Number(e.target.value))}
                          />
                          <Label className="form-check-label" htmlFor="nonConforming">
                            Non-Conforming
                          </Label>
                        </div>
                      </div>
                      <ErrorMessage
                        name="confirming"
                        component="div"
                        className="text-danger mt-1"
                        style={{ width: '100%', textAlign: 'right' }}
                      />
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end">
                    <Button color="secondary" type="button" onClick={toggle}>
                      Cancel
                    </Button>
                    <Button
                      color="success"
                      type="submit"
                      className="ms-2"
                      disabled={isSaveDisabled(isSubmitting)}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </Row>
            )}
          </Form>
        )}
      </Formik>

      {showWarning && (
        <div
          className="warning-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '500px',
              textAlign: 'center',
            }}
          >
            <h4 style={{ color: '#ff9800', marginBottom: '15px' }}>⚠️ Warning</h4>
            <p>There are defects (NOT OK parameters) in the form.</p>
            <p>Do you still want to submit?</p>
            <div style={{ marginTop: '20px' }}>
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => {
                  setShowWarning(false);
                  setPendingSubmit(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => {
                  setShowWarning(false);
                  if (pendingSubmit) {
                    handleSubmit(pendingSubmit.values, {
                      resetForm: pendingSubmit.resetForm,
                    });
                  }
                }}
              >
                OK - Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

AddLuggageRack.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  editData: PropTypes.object,
  onSuccess: PropTypes.func,
};

AddLuggageRack.defaultProps = {
  editData: null,
};

export default AddLuggageRack;
